<template>
  <div class="d-flex flex-column gap-6">
    <div class="d-flex flex-column gap-6">
      <div class="d-flex flex-row flex-wrap gap-3">
        <h1 class="typography-heading-xlg">{{ t('global.specialtyTerms.yardManagement') }}</h1>

        <div class="d-flex flex-row flex-wrap align-center gap-3 ml-auto">
          <yard-view-warehouse-select
            style="min-width: 300px"
            v-model="selectedWarehouse"
            @update:model-value="persistSelectedWarehouse"
            @update:items="loadPreviousSelectedWarehouse" />
        </div>
      </div>

      <v-tabs v-model="tab" color="primary" class="custom-tabs">
        <v-tab value="yard-view">{{ t('yard.tabLabels.yardView') }}</v-tab>
        <v-tab value="yard-tasks" disabled>
          {{ t('yard.tabLabels.yardTasks') }}
          <v-chip density="comfortable" size="x-small" class="ml-2">
            {{ t('global.basicTerms.soon') }}
          </v-chip>
        </v-tab>
      </v-tabs>
    </div>

    <template v-if="hasWarehouses">
      <yard-view-no-selected-warehouse v-if="!selectedWarehouse?.id" />
      <template v-else>
        <v-tabs-window v-model="tab">
          <v-tabs-window-item value="yard-view">
            <div>
              <div class="d-flex flex-column gap-4">
                <yard-view-page-sub-header>
                  <div class="d-flex flex-row flex-wrap flex-md-nowrap justify-lg-end gap-3">
                    <big-number-card
                      :data-testid="`yard-view-page-header-card-${card.title}`"
                      v-for="card of cards"
                      :key="card.title"
                      :label="card.title"
                      orientation="horizontal">
                      {{ card.count }}
                    </big-number-card>
                  </div>
                </yard-view-page-sub-header>

                <yard-view-yard
                  v-if="
                    isYMSEnabledForSelectedWarehouse && isGateManagementEnabledForSelectedWarehouse
                  " />
                <yard-view-gm-disabled v-else-if="!isGateManagementEnabledForSelectedWarehouse" />
                <yard-view-yms-disabled v-else />
              </div>

              <v-spacer></v-spacer>

              <v-navigation-drawer
                v-if="shouldShowNavigationDrawer"
                location="right"
                permanent
                :temporary="isAssetVisitChatVisible"
                class="yard-navigation-drawer"
                :width="isAssetVisitChatVisible ? 756 : 256">
                <transition name="slide-fade" mode="out-in">
                  <yard-view-asset-visit-detail-chat
                    :asset-visit="assetVisitForChat"
                    v-if="isAssetVisitChatVisible" />
                  <template v-else>
                    <yard-view-spot-detail v-if="selectedSpot?.id" />
                    <yard-view-asset-visit-detail v-else-if="selectedAssetVisit?.id" />
                    <yard-view-waiting-assets-list v-else />
                  </template>
                </transition>
              </v-navigation-drawer>
            </div>
            <yard-view-loading v-if="isLoading" />
          </v-tabs-window-item>

          <v-tabs-window-item value="yard-tasks" v-if="isYardTasksEnabled">
            <yard-tasks />
          </v-tabs-window-item>
        </v-tabs-window>
      </template>
    </template>
    <template v-else>
      <yard-view-no-warehouse />
    </template>
  </div>
</template>

<script>
import { defineComponent, onBeforeMount, onBeforeUnmount, computed, ref } from 'vue';
import { first } from 'lodash';

import YardViewWaitingAssetsList from '@/modules/yard/components/YardViewWaitingAssetsList.vue';
import YardViewYard from '@/modules/yard/components/YardViewYard.vue';
import YardTasks from '@/modules/yard/components/YardTasks.vue';

import useYard from '@/modules/yard/composables/useYard';
import useYardTasks from '@/modules/yard/composables/useYardTasks';
import YardViewLoading from '@/modules/yard/components/Overlays/YardViewLoading.vue';
import YardViewGmDisabled from '@/modules/yard/components/Overlays/YardViewGMDisabled.vue';
import YardViewYmsDisabled from '@/modules/yard/components/Overlays/YardViewYmsDisabled.vue';
import YardViewNoWarehouse from '@/modules/yard/components/Overlays/YardViewNoWarehouse.vue';
import YardViewPageSubHeader from '@/modules/yard/components/YardViewPageSubHeader.vue';
import YardViewSpotDetail from '@/modules/yard/components/Spot//Detail/YardViewSpotDetail.vue';
import { useEventHub, useSockets } from '@/composables';
import YardViewAssetVisitDetail from '@/modules/yard/components/AssetVisit/Detail/YardViewAssetVisitDetail.vue';
import { YardViewModeEnum } from '@nova/core';
import YardViewAssetVisitDetailChat from '@/modules/yard/components/AssetVisit/Detail/YardViewAssetVisitDetailChat.vue';
import YardViewNoSelectedWarehouse from '@/modules/yard/components/Overlays/YardViewNoSelectedWarehouse.vue';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'YardViewPage',
  components: {
    YardViewAssetVisitDetail,
    YardViewSpotDetail,
    YardViewPageSubHeader,
    YardViewLoading,
    YardViewYmsDisabled,
    YardViewNoWarehouse,
    YardViewYard,
    YardViewWaitingAssetsList,
    YardViewNoSelectedWarehouse,
    YardViewAssetVisitDetailChat,
    YardViewGmDisabled,
    YardTasks
  },
  props: {},
  setup(_, { emit }) {
    const { t } = useI18n();
    const yardStore = useYard();
    const yardTasks = useYardTasks();

    const sockets = useSockets();
    const eventHub = useEventHub();
    const hasWarehouses = ref(false);
    const tab = ref('yard-view-tab');

    onBeforeMount(() => {
      Object.keys(sockets.actions).forEach(operation => {
        eventHub.$on(`${operation}-AssetVisit`, yardStore.handleAssetVisitSubspaceEvent);
        eventHub.$on(`${operation}-AssetContainer`, yardStore.handleAssetContainerSubspaceEvent);
        eventHub.$on(
          `${operation}-AssetContainerEvent`,
          yardStore.handleAssetContainerEventSubspaceEvent
        );
        eventHub.$on(`${operation}-AssetVisitEvent`, yardStore.handleAssetVisitEventSubspaceEvent);
      });
    });

    onBeforeUnmount(() => {
      Object.keys(sockets.actions).forEach(operation => {
        eventHub.$off(`${operation}-AssetVisit`, yardStore.handleAssetVisitSubspaceEvent);
        eventHub.$off(`${operation}-AssetContainer`, yardStore.handleAssetContainerSubspaceEvent);
        eventHub.$off(
          `${operation}-AssetContainerEvent`,
          yardStore.handleAssetContainerEventSubspaceEvent
        );
        eventHub.$off(`${operation}-AssetVisitEvent`, yardStore.handleAssetVisitEventSubspaceEvent);
      });
    });

    const assetVisitForChat = computed(() => {
      if (yardStore.selectedAssetVisitRef.value) {
        return yardStore.selectedAssetVisitRef.value;
      }

      if (yardStore.selectedSpotRef.value) {
        return yardStore.selectedSpotRef.value?.spotAssignments.find(s => s.assetVisit?.id)
          ?.assetVisit;
      }

      return {};
    });

    const spots = computed(() =>
      yardStore.spotsRef.value.filter(spot =>
        yardStore.selectedAreasRef.value.includes(spot.spotAreaId)
      )
    );

    const parkedCount = computed(
      () =>
        spots.value.filter(
          spot => spot.spotAssignments.length > 0 && spot.type === yardStore.spotType.PARKING
        ).length
    );

    const dockedCount = computed(
      () =>
        spots.value.filter(
          spot => spot.spotAssignments.length > 0 && spot.type === yardStore.spotType.DOCKING
        ).length
    );

    const waitingCount = computed(() => yardStore.waitingAssetVisitsRef.value.length);

    const shouldShowNavigationDrawer = computed(() => {
      return (
        tab.value === 'yard-view' &&
        yardStore.isYMSEnabledForSelectedWarehouse &&
        yardStore.isGateManagementEnabledForSelectedWarehouse &&
        yardStore.viewModeRef.value === YardViewModeEnum.GRID
      );
    });

    const cards = computed(() => {
      const items = [
        {
          title: t('yard.pages.yardView.headers.parked'),
          count: parkedCount.value
        },
        {
          title: t('yard.pages.yardView.headers.docked'),
          count: dockedCount.value
        }
      ];
      if (yardStore.viewModeRef.value === YardViewModeEnum.LIST) {
        items.push({
          title: t('yard.pages.yardView.headers.waiting'),
          count: waitingCount.value
        });
      }
      return items;
    });

    const persistSelectedWarehouse = warehouse =>
      yardStore.persistSelectedWarehouse(warehouse?.id ?? null);

    const loadPreviousSelectedWarehouse = async warehouses => {
      emit('update:warehouse-list', warehouses);

      hasWarehouses.value = Array.isArray(warehouses) && warehouses.length > 0;

      if (!hasWarehouses.value) {
        return;
      }

      const previousData = await yardStore.fetchPreviousYardData();

      // When there is only one warehouse or no previous data, select the first warehouse by default
      if (warehouses.length === 1 || previousData?.selectedWarehouseId == null) {
        const selectedWarehouse = first(warehouses);

        yardStore.selectedWarehouseRef.value = selectedWarehouse;
        persistSelectedWarehouse(selectedWarehouse);

        return;
      }

      if (previousData?.selectedWarehouseId) {
        const warehouse = warehouses.find(
          warehouse => warehouse.id === previousData.selectedWarehouseId
        );

        yardStore.selectedWarehouseRef.value = warehouse;
      }

      // Set the view mode after the warehouse is set
      if (previousData?.viewMode) {
        yardStore.viewModeRef.value = previousData.viewMode;
      }
    };

    return {
      t,
      YardViewModeEnum,
      viewMode: yardStore.viewModeRef,
      selectedWarehouse: yardStore.selectedWarehouseRef,
      isLoading: yardStore.isLoadingSpotsRef,
      selectedSpot: yardStore.selectedSpotRef,
      selectedAssetVisit: yardStore.selectedAssetVisitRef,
      isYMSEnabledForSelectedWarehouse: yardStore.isYMSEnabledForSelectedWarehouse,
      isGateManagementEnabledForSelectedWarehouse:
        yardStore.isGateManagementEnabledForSelectedWarehouse,
      isAssetVisitChatVisible: yardStore.isAssetVisitChatVisibleRef,
      assetVisitForChat,
      hasWarehouses,
      tab,
      cards,
      shouldShowNavigationDrawer,
      persistSelectedWarehouse,
      loadPreviousSelectedWarehouse,
      isYardTasksEnabled: yardTasks.isYardTasksEnabled
    };
  }
});
</script>
<style lang="scss">
.custom-tabs .v-slide-group__content {
  border-bottom: 2px solid $color-line-divider;
}

.header-actions {
  min-height: 48px;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
.yard-navigation-drawer {
  .card-actions {
    position: fixed;
    bottom: 0;
    width: 100%;
    margin-left: 1px;
    background-color: $white;
    z-index: 1000;
  }
  .card-header {
    position: fixed;
    top: 0;
    width: 100%;
    margin-left: 1px;
    background-color: $white;
    z-index: 1000;
  }
  .card-content {
    position: fixed;
    top: 80px;
    bottom: 160px;
    width: 100%;
    margin-left: 1px;
    overflow-y: auto;
  }
  .card-content-no-actions {
    position: fixed;
    top: 80px;
    bottom: 50px;
    width: 100%;
    margin-left: 1px;
    overflow-y: auto;
  }
}
.fix-truck-rotation {
  height: 30px;
  width: 100px;
  transform-origin: 15px 15px;
}
</style>
